'use client'

import clsx from 'clsx'

interface Props {
    /** On/off state (controlled). */
    checked: boolean
    /** Called with the requested next state when the user flips the switch. */
    onCheckedChange: (next: boolean) => void
    disabled?: boolean
    /** Accessible name for the (label-less) switch. Pass a localised string. */
    'aria-label'?: string
    className?: string
}

/**
 * Switch — a controlled on/off toggle (the ARIA `switch` role), styled with Tailwind.
 *
 * A generic building block: it takes `checked` / `onCheckedChange` and renders a track + sliding
 * thumb, no i18n and no use-case styling. Callers own the surrounding label and pass an
 * `aria-label`. For expand/collapse behaviour use `Toggle` instead — this is a boolean setting
 * control, not a disclosure.
 */
const Switch = ({
    checked,
    onCheckedChange,
    disabled = false,
    'aria-label': ariaLabel,
    className,
}: Props) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => onCheckedChange(!checked)}
        className={clsx(
            'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            checked ? 'bg-primary-500' : 'bg-grey-600',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            className,
        )}>
        <span
            className={clsx(
                'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
                checked ? 'translate-x-[22px]' : 'translate-x-[2px]',
            )}
        />
    </button>
)

export default Switch
